import React, { useState } from 'react';
import { parseNlesFile, validateLesson } from '../../utils/lessonParser';

const LessonUploader = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [file, setFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [editorContent, setEditorContent] = useState(`---
title: New Lesson Title
duration: 45 mins
category: AI Fundamentals
difficulty: Beginner
tags: tag1, tag2, tag3
---

# New Lesson Title

Start writing your lesson content here. This uses Markdown formatting.

## Section Heading

Regular paragraph text goes here.

- List item 1
- List item 2
- List item 3

### Subsection

More content...`);
  const [fileName, setFileName] = useState('');
  const [courseId, setCourseId] = useState('1');
  const [previewData, setPreviewData] = useState(null);
  const [errors, setErrors] = useState([]);
  const [message, setMessage] = useState('');

  // List of available courses for dropdown
  const courses = [
    { id: '1', title: 'AI Fundamentals' },
    { id: '2', title: 'Prompt Engineering Mastery' }
  ];

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setFileName(selectedFile.name);
    
    // Read file content
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setFileContent(content);
      validateContent(content);
    };
    reader.readAsText(selectedFile);
  };

  // Validate content whether from file or editor
  const validateContent = (content) => {
    try {
      setErrors([]);
      const parsedLesson = parseNlesFile(content);
      const isValid = validateLesson(parsedLesson);
      
      if (isValid) {
        setPreviewData(parsedLesson);
        setMessage('Lesson is valid and ready to upload!');
      } else {
        setPreviewData(null);
        setMessage('');
      }
    } catch (error) {
      setErrors([error.message]);
      setPreviewData(null);
      setMessage('');
    }
  };

  // Preview editor content
  const handlePreviewEditor = () => {
    validateContent(editorContent);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const contentToUpload = activeTab === 'upload' ? fileContent : editorContent;
    
    try {
      // Validate again just to be sure
      const parsedLesson = parseNlesFile(contentToUpload);
      const isValid = validateLesson(parsedLesson);
      
      if (!isValid) {
        setMessage('Invalid lesson format. Please fix the errors before uploading.');
        return;
      }
      
      // In a real app, this would send to an API endpoint
      // For demo purposes, we'll just show a success message
      
      // Create destination filename if not from file upload
      const finalFileName = activeTab === 'upload' 
        ? fileName 
        : `${parsedLesson.metadata.title.toLowerCase().replace(/\s+/g, '-')}.nles`;
      
      // Simulate successful upload
      setMessage(`Success! Lesson "${parsedLesson.metadata.title}" would be uploaded as ${finalFileName} to course ID ${courseId}`);
      
      // Clear form in create mode
      if (activeTab === 'create') {
        setEditorContent(`---
title: New Lesson Title
duration: 45 mins
category: AI Fundamentals
difficulty: Beginner
tags: tag1, tag2, tag3
---

# New Lesson Title

Start writing your lesson content here. This uses Markdown formatting.

## Section Heading

Regular paragraph text goes here.

- List item 1
- List item 2
- List item 3

### Subsection

More content...`);
      }
      
      // Clear file in upload mode
      if (activeTab === 'upload') {
        setFile(null);
        setFileContent('');
        setFileName('');
      }
      
      setPreviewData(null);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-heading font-bold text-gray-900 dark:text-white mb-6">
        Lesson Management
      </h1>
      
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          className={`py-3 px-6 ${
            activeTab === 'upload'
              ? 'border-b-2 border-primary-600 text-primary-600 dark:text-primary-400 font-medium'
              : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
          }`}
          onClick={() => setActiveTab('upload')}
        >
          Upload Lesson File
        </button>
        <button
          className={`py-3 px-6 ${
            activeTab === 'create'
              ? 'border-b-2 border-primary-600 text-primary-600 dark:text-primary-400 font-medium'
              : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
          }`}
          onClick={() => setActiveTab('create')}
        >
          Create New Lesson
        </button>
      </div>
      
      {/* Form */}
      <form onSubmit={handleSubmit}>
        {/* Course selection */}
        <div className="mb-6">
          <label htmlFor="course" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Course
          </label>
          <select
            id="course"
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            {courses.map(course => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
        </div>
        
        {/* Upload section */}
        {activeTab === 'upload' && (
          <div className="mb-6">
            <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Upload .nles File
            </label>
            <div className="mt-1 flex items-center">
              <label className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-700 border-dashed rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {fileName || 'Click to browse or drop a file here'}
                </span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  accept=".nles,.md,.txt"
                  className="sr-only"
                  onChange={handleFileChange}
                />
              </label>
            </div>
          </div>
        )}
        
        {/* Create section */}
        {activeTab === 'create' && (
          <div className="mb-6">
            <label htmlFor="editor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Lesson Content Editor
            </label>
            <div className="rounded-md overflow-hidden border border-gray-300 dark:border-gray-700 mb-2">
              <textarea
                id="editor"
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                className="w-full h-96 p-4 font-mono text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={handlePreviewEditor}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Preview & Validate
            </button>
          </div>
        )}
        
        {/* Errors display */}
        {errors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-md">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-400 mb-2">
              Please fix the following errors:
            </h3>
            <ul className="list-disc pl-5 text-sm text-red-700 dark:text-red-300">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Preview section */}
        {previewData && (
          <div className="mb-6 p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Lesson Preview
            </h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </h4>
                <p className="text-gray-900 dark:text-white">
                  {previewData.metadata.title}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Duration
                </h4>
                <p className="text-gray-900 dark:text-white">
                  {previewData.metadata.duration}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </h4>
                <p className="text-gray-900 dark:text-white">
                  {previewData.metadata.category}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Difficulty
                </h4>
                <p className="text-gray-900 dark:text-white">
                  {previewData.metadata.difficulty}
                </p>
              </div>
              
              {previewData.metadata.tags && (
                <div className="col-span-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tags
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {previewData.metadata.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Content Preview (first 100 characters)
            </h4>
            <p className="text-gray-600 dark:text-gray-400 text-sm whitespace-pre-wrap">
              {previewData.content.substring(0, 100)}...
            </p>
          </div>
        )}
        
        {/* Submit button */}
        <div className="flex justify-between items-center">
          <button
            type="submit"
            className="px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            disabled={activeTab === 'upload' && !file}
          >
            {activeTab === 'upload' ? 'Upload Lesson' : 'Save Lesson'}
          </button>
          
          {message && (
            <p className={`text-sm ${message.includes('Error') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {message}
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default LessonUploader; 